import type { HttpClient, HttpParams } from '@angular/common/http';
import { map, type Observable } from 'rxjs';

/**
 * Base class for API clients that interact with a REST-ful API.
 *
 * @template T - Domain entity type consumed by stores and the UI.
 * @template TWire - Raw backend payload type used for HTTP generics.
 *   Defaults to {@link T}. Override {@link toWire} / {@link fromWire} when
 *   the wire shape differs from the domain model (e.g. Rails envelopes,
 *   legacy field renames). {@link fromWire} accepts `unknown` so clients
 *   that map a distinct wire shape stay assignable to {@link BaseApiClient}<T>.
 */
export abstract class BaseApiClient<T, TWire = T> {
  /** The HTTP client used to make API requests. */
  protected abstract http: HttpClient;
  /** Optional entity template with default values used when creating a new entity. */
  protected abstract template?: Partial<T>;
  /** The URL of the API endpoint. */
  protected abstract url: string;

  /**
   * Maps a raw backend payload to the domain entity.
   * Identity by default — override for envelopes or field renames.
   * Accepts `unknown` so subclasses that normalize a distinct wire shape
   * remain assignable to {@link BaseApiClient}<T> (store inject sites).
   */
  protected fromWire(wire: unknown): T {
    // Identity default when TWire = T; HTTP JSON is untyped at the boundary.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- identity map
    return wire as T;
  }

  /**
   * Maps a domain entity to the request body the backend expects.
   * Identity by default — override for envelopes or field renames.
   */
  protected toWire(entity: Partial<T>): unknown {
    return entity;
  }

  /**
   * Adds a new entity to the API.
   * @param entity - The entity to add.
   * @param [urlSegments] - Optional URL segments.
   * @returns An observable that emits the added entity.
   */
  add(
    entity: Partial<T>,
    urlSegments?: Record<string, string | number>
  ): Observable<T>;
  /**
   * Adds multiple entities to the API.
   * @param entities - The entities to add.
   * @param [urlSegments] - Optional URL segments.
   * @returns An observable that emits an array of added entities.
   */
  add(
    entities: Partial<T>[],
    urlSegments?: Record<string, string | number>
  ): Observable<T[]>;
  /** Implementation of the `add` overloads. */
  add(
    data: Partial<T> | Partial<T>[],
    urlSegments?: Record<string, string | number>
  ): Observable<T | T[]> {
    if (Array.isArray(data)) {
      const entities = data.map((entity) => ({
        ...this.template,
        ...entity,
      }));
      return this.http
        .post<TWire[]>(
          this.getFormattedUrl(urlSegments),
          entities.map((entity) => this.toWire(entity))
        )
        .pipe(map((wires) => wires.map((wire) => this.fromWire(wire))));
    }
    return this.http
      .post<TWire>(
        this.getFormattedUrl(urlSegments),
        this.toWire({
          ...this.template,
          ...data,
        })
      )
      .pipe(map((wire) => this.fromWire(wire)));
  }

  /**
   * Deletes an entity from the API.
   * @param id - The ID of the entity to delete.
   * @param [urlSegments] - Optional URL segments.
   * @returns An observable that emits void when the deletion is successful.
   */
  delete(
    id: string | number,
    urlSegments?: Record<string, string | number>
  ): Observable<void> {
    return this.http.delete<void>(`${this.getFormattedUrl(urlSegments)}/${id}`);
  }

  /**
   * Retrieves all entities from the API.
   * @param [urlSegments] - Optional URL segments.
   * @param [params] - Optional HTTP query parameters.
   * @returns An observable that emits an array of entities.
   */
  getAll(
    urlSegments?: Record<string, string | number>,
    params?: HttpParams
  ): Observable<T[]> {
    return this.http
      .get<TWire[]>(this.getFormattedUrl(urlSegments), {
        params,
      })
      .pipe(map((wires) => wires.map((wire) => this.fromWire(wire))));
  }

  /**
   * Retrieves a specific entity from the API by its ID.
   * @param id - The ID of the entity to retrieve.
   * @param [urlSegments] - Optional URL segments.
   * @returns An observable that emits the retrieved entity.
   */
  getById(
    id: string | number,
    urlSegments?: Record<string, string | number>
  ): Observable<T> {
    return this.http
      .get<TWire>(`${this.getFormattedUrl(urlSegments)}/${id}`)
      .pipe(map((wire) => this.fromWire(wire)));
  }

  /**
   * Formats the URL with the given URL segments.
   *
   * This allows to use a parameterized URL in the `url` class attribute.
   * @param [urlSegments] - Optional URL segments to substitute into the URL template.
   * @returns The URL with all `{segment}` placeholders replaced.
   * @example With URL `'/entities/{entityId}/sub-entities'`:
   * ```ts
   * // Returns '/entities/1/sub-entities'.
   * getFormattedUrl({ entityId: '1' });
   * ```
   */
  getFormattedUrl(urlSegments?: Record<string, string | number>): string {
    let formattedUrl: string = this.url;
    if (!urlSegments) {
      return formattedUrl;
    }
    Object.keys(urlSegments).forEach((key) => {
      formattedUrl = formattedUrl.replaceAll(
        `{${key}}`,
        String(urlSegments[key])
      );
    });
    return formattedUrl;
  }

  /**
   * Partially updates an existing entity in the API.
   * @param id - The ID of the entity to update.
   * @param entity - The partial entity to update.
   * @param [urlSegments] - Optional URL segments.
   * @returns An observable that emits the updated entity.
   */
  patch(
    id: string | number,
    entity: Partial<T>,
    urlSegments?: Record<string, string | number>
  ): Observable<T> {
    return this.http
      .patch<TWire>(
        `${this.getFormattedUrl(urlSegments)}/${id}`,
        this.toWire(entity)
      )
      .pipe(map((wire) => this.fromWire(wire)));
  }

  /**
   * Updates an existing entity in the API.
   * @param id - The ID of the entity to update.
   * @param entity - The entity to update.
   * @param [urlSegments] - Optional URL segments.
   * @returns An observable that emits the updated entity.
   */
  update(
    id: string | number,
    entity: T,
    urlSegments?: Record<string, string | number>
  ): Observable<T> {
    return this.http
      .put<TWire>(
        `${this.getFormattedUrl(urlSegments)}/${id}`,
        this.toWire(entity)
      )
      .pipe(map((wire) => this.fromWire(wire)));
  }
}
